import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, FileText, History, IndianRupee, Percent, TrendingUp, Wallet } from 'lucide-react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BillingFiltersBar } from '../../../components/MasterAdmin/BillingAnalytics/BillingFiltersBar';
import {
  chartTooltipStyle,
  formatCompact,
  formatCurrency,
  formatDateShort,
  paletteColor,
  tdClass,
  thClass,
} from '../../../components/MasterAdmin/BillingAnalytics/billingUi';
import InsightsPageShell from '../../../components/MasterAdmin/insights/InsightsPageShell';
import type { MetricInfoDefinition } from '../../../components/MasterAdmin/ui/MetricInfoPopover';
import OperationsStatCard from '../../../components/MasterAdmin/operations/OperationsStatCard';
import { OpsChartTitle } from '../../../components/MasterAdmin/operations/OpsMetricLabels';
import { CHART_COLORS, opsChartCard, opsMetricGridClass } from '../../../components/MasterAdmin/operations/operationsUi';
import { getBillingHistory, getTimeRangeDates } from '../../../services/MasterAdmin/billingAnalyticsService';

const PAGE_INFO: MetricInfoDefinition = {
  definition: 'Day-by-day and month-by-month GCP cost history from BigQuery billing export. Use this for invoice-style monthly totals.',
  sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['usage_date', 'cost', 'credits'] }],
};

type HistoryRow = {
  date: string;
  label: string;
  grossCost: number;
  credits: number;
  netCost: number;
  currency: string;
};

type MonthRow = {
  key: string;
  label: string;
  days: number;
  grossCost: number;
  credits: number;
  netCost: number;
  avgDaily: number;
};

type HistoryTab = 'daily' | 'monthly';

const BillingHistoryPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const timeRange = searchParams.get('timeRange') || 'This Month';
  const projectId = searchParams.get('projectId') || 'vedika-health';
  const customStart = searchParams.get('startDate') || undefined;
  const customEnd = searchParams.get('endDate') || undefined;
  const serviceId = searchParams.get('serviceId') || undefined;
  const resourceName = searchParams.get('resourceName') || undefined;

  const [activeTab, setActiveTab] = useState<HistoryTab>('daily');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<HistoryRow[]>([]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const { start, end } = getTimeRangeDates(timeRange, customStart, customEnd);
        const data = await getBillingHistory(start, end, projectId, serviceId, resourceName);
        setHistoryData(
          (data || []).map((row: any) => ({
            date: row.date,
            label: row.date ? new Date(row.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '',
            grossCost: Number(row.grossCost || 0),
            credits: Math.abs(Number(row.credits || 0)),
            netCost: Number(row.netCost || 0),
            currency: row.currency || 'INR',
          }))
        );
      } catch (err) {
        console.error('Failed to fetch history data:', err);
        setError('Unable to load billing history. Please retry.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [timeRange, projectId, customStart, customEnd, serviceId, resourceName]);

  const currency = historyData[0]?.currency || 'INR';

  const totals = useMemo(() => {
    const net = historyData.reduce((s, r) => s + r.netCost, 0);
    const gross = historyData.reduce((s, r) => s + r.grossCost, 0);
    const credits = historyData.reduce((s, r) => s + r.credits, 0);
    const days = Math.max(1, historyData.length);
    const avgDaily = net / days;
    const peak = historyData.reduce<HistoryRow | null>((best, row) => (!best || row.netCost > best.netCost ? row : best), null);
    const low = historyData
      .filter((r) => r.netCost > 0)
      .reduce<HistoryRow | null>((best, row) => (!best || row.netCost < best.netCost ? row : best), null);
    return { net, gross, credits, days: historyData.length, avgDaily, peak, low, savingsRate: gross > 0 ? (credits / gross) * 100 : 0 };
  }, [historyData]);

  const monthly = useMemo<MonthRow[]>(() => {
    const map = new Map<string, MonthRow>();
    historyData.forEach((row) => {
      if (!row.date) return;
      const d = new Date(row.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const cur = map.get(key) || {
        key,
        label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
        days: 0,
        grossCost: 0,
        credits: 0,
        netCost: 0,
        avgDaily: 0,
      };
      cur.days += 1;
      cur.grossCost += row.grossCost;
      cur.credits += row.credits;
      cur.netCost += row.netCost;
      map.set(key, cur);
    });
    return Array.from(map.values())
      .map((m) => ({ ...m, avgDaily: m.days > 0 ? m.netCost / m.days : 0 }))
      .sort((a, b) => b.key.localeCompare(a.key));
  }, [historyData]);

  const chartData = useMemo(
    () =>
      historyData.map((row) => ({
        name: row.label,
        fullDate: row.date,
        netCost: row.netCost,
        grossCost: row.grossCost,
        credits: row.credits,
      })),
    [historyData]
  );

  const monthChart = useMemo(
    () =>
      [...monthly].reverse().map((m, idx) => ({
        name: m.label.replace(/ \d{4}$/, '').slice(0, 3),
        fullName: m.label,
        netCost: m.netCost,
        credits: m.credits,
        fill: paletteColor(idx),
      })),
    [monthly]
  );

  const dailyNewestFirst = useMemo(() => [...historyData].reverse(), [historyData]);

  return (
    <InsightsPageShell
      title="Billing History"
      description="Daily cost ledger and month-by-month invoice-style totals from GCP billing export."
      info={PAGE_INFO}
    >
      <BillingFiltersBar />

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-900/40 dark:bg-red-900/20">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      <div className="flex w-full items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-900/60 sm:w-fit">
        <button
          type="button"
          onClick={() => setActiveTab('daily')}
          className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-medium transition-all ${
            activeTab === 'daily' ? 'bg-white text-primary-600 shadow-sm dark:bg-gray-800' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <History className="h-3.5 w-3.5" /> Daily ledger
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('monthly')}
          className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-medium transition-all ${
            activeTab === 'monthly' ? 'bg-white text-primary-600 shadow-sm dark:bg-gray-800' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <FileText className="h-3.5 w-3.5" /> Monthly invoice
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <div className={opsMetricGridClass}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
            ))}
          </div>
          <div className="h-72 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className={opsMetricGridClass}>
            <OperationsStatCard label="Net cost" infoKey="totalCostNet" value={formatCurrency(totals.net, currency)} sub={`${totals.days} billed day${totals.days === 1 ? '' : 's'}`} icon={IndianRupee} />
            <OperationsStatCard label="Gross cost" infoKey="totalGrossCost" value={formatCurrency(totals.gross, currency)} sub="Before credits" icon={Wallet} />
            <OperationsStatCard
              label="Credits saved"
              infoKey="totalCredits"
              value={formatCurrency(totals.credits, currency)}
              sub={`${totals.savingsRate.toFixed(1)}% of list price`}
              icon={Percent}
              accent="text-emerald-600 dark:text-emerald-400"
            />
            <OperationsStatCard
              label="Avg daily spend"
              infoKey="avgDailyCost"
              value={formatCurrency(totals.avgDaily, currency)}
              sub={totals.peak ? `Peak ${formatDateShort(totals.peak.date)}` : 'No daily data'}
              icon={TrendingUp}
            />
          </div>

          {activeTab === 'daily' && (
            <>
              <div className={opsChartCard}>
                <OpsChartTitle title="Daily spend" infoKey="billingTrend" />
                {chartData.length === 0 ? (
                  <p className="py-16 text-center text-sm text-gray-500">No daily history in this period</p>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="histNetFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={CHART_COLORS.primary} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompact} width={56} />
                        <Tooltip
                          {...chartTooltipStyle}
                          formatter={(value: number, name: string) => [formatCurrency(value, currency), name]}
                          labelFormatter={(_, payload) => (payload?.[0]?.payload?.fullDate ? formatDateShort(payload[0].payload.fullDate) : '')}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Area type="monotone" dataKey="netCost" name="Net cost" stroke={CHART_COLORS.primary} fill="url(#histNetFill)" strokeWidth={2.5} />
                        <Area type="monotone" dataKey="grossCost" name="Gross cost" stroke={CHART_COLORS.purple} fill="transparent" strokeWidth={2} strokeDasharray="4 4" />
                        <Bar dataKey="credits" name="Credits" fill={CHART_COLORS.healthy} maxBarSize={18} radius={[4, 4, 0, 0]} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
                <p className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">Daily cost ledger</p>
                <p className="mb-4 text-xs text-gray-500">Days above the period average are flagged. Peak day is highlighted.</p>
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/80">
                      <tr>
                        <th className={`${thClass} text-left`}>Date</th>
                        <th className={`${thClass} text-right`}>Gross</th>
                        <th className={`${thClass} text-right`}>Credits</th>
                        <th className={`${thClass} text-right`}>Net cost</th>
                        <th className={`${thClass} text-right`}>vs daily avg</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {dailyNewestFirst.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                            No history data available for this time range
                          </td>
                        </tr>
                      ) : (
                        dailyNewestFirst.map((row) => {
                          const vsAvg = totals.avgDaily > 0 ? ((row.netCost - totals.avgDaily) / totals.avgDaily) * 100 : 0;
                          const isPeak = totals.peak?.date === row.date;
                          return (
                            <tr key={row.date} className={`bg-white dark:bg-gray-900/40 ${isPeak ? 'bg-amber-50/70 dark:bg-amber-950/20' : ''}`}>
                              <td className={`${tdClass} font-medium`}>
                                {formatDateShort(row.date)}
                                {isPeak && (
                                  <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                    Peak
                                  </span>
                                )}
                              </td>
                              <td className={`${tdClass} text-right font-mono text-gray-500`}>{formatCurrency(row.grossCost, currency)}</td>
                              <td className={`${tdClass} text-right font-mono text-emerald-600 dark:text-emerald-400`}>
                                {formatCurrency(row.credits, currency)}
                              </td>
                              <td className={`${tdClass} text-right font-mono font-semibold`}>{formatCurrency(row.netCost, currency)}</td>
                              <td className={`${tdClass} text-right`}>
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                    vsAvg > 8
                                      ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                                      : vsAvg < -8
                                        ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                                  }`}
                                >
                                  {vsAvg > 0 ? '+' : ''}
                                  {vsAvg.toFixed(0)}%
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {dailyNewestFirst.length > 0 && (
                      <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-semibold dark:border-gray-700 dark:bg-gray-800/80">
                        <tr>
                          <td className="px-4 py-3 text-xs uppercase tracking-wide text-gray-500">Total</td>
                          <td className="px-4 py-3 text-right font-mono">{formatCurrency(totals.gross, currency)}</td>
                          <td className="px-4 py-3 text-right font-mono text-emerald-600">{formatCurrency(totals.credits, currency)}</td>
                          <td className="px-4 py-3 text-right font-mono text-primary-600">{formatCurrency(totals.net, currency)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </>
          )}

          {activeTab === 'monthly' && (
            <>
              <div className={opsChartCard}>
                <OpsChartTitle title="Monthly net cost" infoKey="dailyCostTable" />
                {monthChart.length === 0 ? (
                  <p className="py-16 text-center text-sm text-gray-500">No months in this period — widen the date range</p>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthChart}>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompact} width={56} />
                        <Tooltip
                          {...chartTooltipStyle}
                          formatter={(value: number, name: string) => [formatCurrency(value, currency), name]}
                          labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Bar dataKey="netCost" name="Net cost" radius={[6, 6, 0, 0]} maxBarSize={48}>
                          {monthChart.map((entry) => (
                            <Cell key={entry.fullName} fill={entry.fill} />
                          ))}
                        </Bar>
                        <Bar dataKey="credits" name="Credits" fill={CHART_COLORS.healthy} radius={[6, 6, 0, 0]} maxBarSize={48} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
                <p className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">Invoice-style monthly totals</p>
                <p className="mb-4 text-xs text-gray-500">
                  Rolled up from daily BigQuery export for project <span className="font-semibold text-gray-700 dark:text-gray-200">{projectId}</span>.
                  Official invoices still come from the Google Cloud Billing Console.
                </p>
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/80">
                      <tr>
                        <th className={`${thClass} text-left`}>Month</th>
                        <th className={`${thClass} text-center`}>Days</th>
                        <th className={`${thClass} text-right`}>Gross</th>
                        <th className={`${thClass} text-right`}>Credits</th>
                        <th className={`${thClass} text-right`}>Net (subtotal)</th>
                        <th className={`${thClass} text-right`}>Avg / day</th>
                        <th className={`${thClass} text-left`}>Share of range</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {monthly.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                            No monthly totals — try Last Month or a custom range that spans more days
                          </td>
                        </tr>
                      ) : (
                        monthly.map((m) => {
                          const share = totals.net > 0 ? (m.netCost / totals.net) * 100 : 0;
                          return (
                            <tr key={m.key} className="bg-white dark:bg-gray-900/40">
                              <td className={`${tdClass} font-semibold`}>{m.label}</td>
                              <td className={`${tdClass} text-center`}>{m.days}</td>
                              <td className={`${tdClass} text-right font-mono text-gray-500`}>{formatCurrency(m.grossCost, currency)}</td>
                              <td className={`${tdClass} text-right font-mono text-emerald-600 dark:text-emerald-400`}>
                                {formatCurrency(m.credits, currency)}
                              </td>
                              <td className={`${tdClass} text-right font-mono font-semibold`}>{formatCurrency(m.netCost, currency)}</td>
                              <td className={`${tdClass} text-right font-mono text-gray-500`}>{formatCurrency(m.avgDaily, currency)}</td>
                              <td className={tdClass}>
                                <div className="flex min-w-[88px] items-center gap-2">
                                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                    <div className="h-full rounded-full bg-primary-600" style={{ width: `${Math.min(100, share)}%` }} />
                                  </div>
                                  <span className="w-10 text-right text-xs font-semibold tabular-nums">{share.toFixed(1)}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {monthly.length > 0 && (
                      <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-semibold dark:border-gray-700 dark:bg-gray-800/80">
                        <tr>
                          <td className="px-4 py-3 text-xs uppercase tracking-wide text-gray-500">Invoice total</td>
                          <td className="px-4 py-3 text-center">{totals.days}</td>
                          <td className="px-4 py-3 text-right font-mono">{formatCurrency(totals.gross, currency)}</td>
                          <td className="px-4 py-3 text-right font-mono text-emerald-600">{formatCurrency(totals.credits, currency)}</td>
                          <td className="px-4 py-3 text-right font-mono text-lg text-primary-600">{formatCurrency(totals.net, currency)}</td>
                          <td className="px-4 py-3 text-right font-mono text-gray-500">{formatCurrency(totals.avgDaily, currency)}</td>
                          <td className="px-4 py-3 text-xs text-gray-400">100%</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </InsightsPageShell>
  );
};

export default BillingHistoryPage;
